/**
 * PlacetelCallButton — Reusable Click-to-Call Button für Telesales-Agents.
 *
 * Verhalten:
 * - Lädt beim Mount `GET /placetel/status` (cached für 60s über sessionStorage)
 *   um zu wissen, ob der eingeloggte User Click-to-Call nutzen kann.
 * - Wenn `can_call=true` → grünes Phone-Icon, Klick → `POST /placetel/call`,
 *   Toast „Anruf wird aufgebaut".
 * - Wenn nicht konfiguriert → ausgegraut mit Tooltip-Erklärung.
 *
 * Props:
 *   phone      string  Zielrufnummer (E.164 ODER 0-Format — Backend normalisiert)
 *   leadId     string? Optional: ID des Submissions/Leads für Logging
 *   size       "sm"|"md" Default "sm"
 *   variant    "icon"|"text"|"full"  Default "icon" (kompaktes Telefon-Icon)
 *   className  string? Zusätzliche Klassen
 */
import { useEffect, useState } from "react";
import { Phone, Loader2, PhoneOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_CACHE_KEY = "placetel_status_cache_v1";
const STATUS_CACHE_TTL = 60_000;  // 60s

async function getStatusCached() {
  try {
    const raw = sessionStorage.getItem(STATUS_CACHE_KEY);
    if (raw) {
      const { ts, data } = JSON.parse(raw);
      if (Date.now() - ts < STATUS_CACHE_TTL) return data;
    }
  } catch { /* ignore */ }
  const { data } = await api.get("/placetel/status");
  try {
    sessionStorage.setItem(STATUS_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch { /* ignore */ }
  return data;
}

export function PlacetelCallButton({
  phone, leadId = null, size = "sm", variant = "icon", className = "",
  onCallStarted = null,
}) {
  const [status, setStatus] = useState(null);
  const [calling, setCalling] = useState(false);

  useEffect(() => {
    getStatusCached().then(setStatus).catch(() => setStatus({ can_call: false }));
  }, []);

  if (!phone) return null;

  const canCall = status?.can_call === true;
  const baseSize = size === "md" ? "h-10 px-3" : "h-8 px-2.5";
  const iconSize = size === "md" ? 16 : 13;

  const handleCall = async (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (!canCall || calling) return;
    setCalling(true);
    try {
      const r = await api.post("/placetel/call", { target: phone, lead_id: leadId });
      toast.success("Anruf wird aufgebaut", {
        description: r.data.message || "Bitte am Telefon abnehmen.",
      });
      onCallStarted?.(r.data);
    } catch (err) {
      const msg = err.response?.data?.detail || "Anruf fehlgeschlagen";
      toast.error("Click-to-Call", { description: msg });
    } finally {
      setCalling(false);
    }
  };

  // Loading state
  if (status == null) {
    return (
      <button
        type="button"
        disabled
        className={`inline-flex items-center gap-1.5 ${baseSize} rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-500 ${className}`}
      >
        <Loader2 className="animate-spin" size={iconSize} />
      </button>
    );
  }

  // Not configured: grayed out with tooltip
  if (!canCall) {
    const reason = !status.configured
      ? "Placetel-Integration nicht konfiguriert."
      : "Dein Account ist nicht mit Placetel verknüpft. Profil → 'Placetel-Konto verknüpfen'.";
    return (
      <button
        type="button"
        disabled
        title={reason}
        data-testid="placetel-call-disabled"
        className={`inline-flex items-center gap-1.5 ${baseSize} rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-600 cursor-not-allowed ${className}`}
      >
        <PhoneOff size={iconSize} />
        {variant !== "icon" && <span className="text-[11.5px] font-bold">Anrufen</span>}
      </button>
    );
  }

  // Active C2C button
  return (
    <button
      type="button"
      onClick={handleCall}
      disabled={calling}
      data-testid="placetel-call-btn"
      title={`Über Placetel anrufen: ${phone}`}
      className={`inline-flex items-center gap-1.5 ${baseSize} rounded-lg bg-[#00E575]/10 hover:bg-[#00E575]/20 border border-[#00E575]/40 text-[#00E575] font-bold transition disabled:opacity-50 ${className}`}
    >
      {calling ? <Loader2 className="animate-spin" size={iconSize} /> : <Phone size={iconSize} />}
      {variant !== "icon" && (
        <span className="text-[11.5px]">{calling ? "Wähle…" : "Anrufen"}</span>
      )}
    </button>
  );
}

export default PlacetelCallButton;
